import { ArrowRight, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { PAGE_GUIDANCE } from "@/content/guidance/pageGuidance";
import { usePageGuidance } from "@/contexts/pageGuidanceContextValue";
import { useRedesignNav } from "@/lib/redesignNav";
import { retiredLink } from "@/lib/redesignRoutes";
import { SectionExplainer } from "@/components/onboarding/financial-analysis/shared/SectionExplainer";

export function PageGuidancePanel() {
  const { pageId, enabled } = usePageGuidance();
  const redesign = useRedesignNav();
  if (!pageId || !enabled) return null;

  const guide = PAGE_GUIDANCE[pageId];

  return (
    <aside
      aria-label={`${guide.title} guide`}
      className="mb-5 rounded-2xl border border-brand-accent/20 bg-card p-4 shadow-sm sm:p-5"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent">
          <BookOpen className="h-4 w-4" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
            Explain this page
          </p>
          <h2 className="text-base font-semibold text-foreground">What {guide.title} is for</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{guide.purpose}</p>
          {"action" in guide && guide.action.destination === "forecast" && (
            // FP-102 instance (T3-PUB-02): "Adjust your buckets" landed at the top of
            // Forecast; the buckets are §2, which the Surplus card and Goals already
            // link to by its anchor (Forecast honours the hash).
            <Link
              to={retiredLink("/forecast#forecast-allocate", redesign)}
              className="tap-target mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              {guide.action.label}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>

      {guide.sections.length > 0 && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {guide.sections.map((section) => (
            <section key={section.id} aria-labelledby={`guide-${pageId}-${section.id}`}>
              <h3
                id={`guide-${pageId}-${section.id}`}
                className="mb-1.5 text-xs font-semibold text-foreground"
              >
                {section.title}
              </h3>
              <SectionExplainer text={section.note} />
            </section>
          ))}
        </div>
      )}

      {pageId === "financialAnalysis" && (
        <p className="mt-3 text-xs text-muted-foreground">
          The approved explanations now appear inside the analysis sections they describe.
        </p>
      )}
    </aside>
  );
}
